import React, { useState } from "react";
import { Menu, X, Plus, Edit, Trash2, Printer, Search as SearchIcon } from "lucide-react";
import AddComponent from "./Add";
import DynamicEditComponent from "./Edit";
import DeleteComponent from "./Delete";
import { Print } from "./Print";
import Search from "./Search";

const ActionBar = ({
  apiEndpoint,
  selectedRow,
  data,
  refreshData,
  rowKey = "id",
  deleteMessage = "Are you sure you want to delete this item?",
  searchQuery,
  userRole,
  setSearchQuery,
}) => {
  const [modals, setModals] = useState({
    add: false,
    edit: false,
    delete: false,
    mobileMenu: false,
    search: false,
  });
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const isAdmin = userRole === "admin";

  const toggleModal = (type, isOpen) => {
    setModals((prev) => ({ 
      ...Object.fromEntries(Object.keys(prev).map(k => [k, false])), 
      [type]: isOpen 
    }));
  };

  const handleDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    toggleModal("delete", true);
  };

  const handleCancelDelete = () => {
    setSelectedDeleteId(null);
    toggleModal("delete", false);
  };

  const renderModal = (type) => {
    switch (type) {
      case "add":
        return (
          <AddComponent
            apiEndpoint={apiEndpoint}
            onSuccess={() => {
              refreshData();
              toggleModal("add", false);
            }}
            onClose={() => toggleModal("add", false)}
          />
        );
      case "edit":
        return (
          <DynamicEditComponent
            data={selectedRow}
            apiEndpoint={apiEndpoint}
            onSave={() => {
              refreshData();
              toggleModal("edit", false);
            }}
            onClose={() => toggleModal("edit", false)}
          />
        );
      case "delete":
        return (
          <DeleteComponent
            itemId={selectedDeleteId}
            apiEndpoint={apiEndpoint}
            onDelete={refreshData}
            onClose={handleCancelDelete}
            deleteMessage={deleteMessage}
          />
        );
      case "search":
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white w-full max-w-md rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Search</h2>
                <button 
                  onClick={() => toggleModal("search", false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={24} />
                </button>
              </div>
              <Search 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery} 
                fullWidth 
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      {/* Mobile Menu Toggle */}
      <div className="flex justify-between items-center bg-gray-100 p-4 md:hidden">
        <button 
          onClick={() => toggleModal("mobileMenu", true)}
          className="text-gray-700"
        >
          <Menu size={24} />
        </button>
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => toggleModal("search", true)}
            className="text-gray-700"
          >
            <SearchIcon size={20} />
          </button>
          <Print data={data} />
        </div>
      </div>

      {/* Desktop Actions */}
      <div className="hidden md:flex items-center gap-4 p-4 bg-gray-100 shadow-md">
        {isAdmin && (
          <>
            <button
              onClick={() => toggleModal("add", true)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Add</span>
            </button>
            <button
              onClick={() => {
                if (!selectedRow) {
                  alert("Please select a row to edit!");
                  return;
                }
                toggleModal("edit", true);
              }}
              className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center space-x-2"
            >
              <Edit size={16} />
              <span>Edit</span>
            </button>
            <button
              onClick={() => {
                if (!selectedRow) {
                  alert("Please select a row to delete!");
                  return;
                }
                handleDeleteConfirmation(selectedRow[rowKey]);
              }}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center space-x-2"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </>
        )}
        <Print data={data} />
        <div className="ml-auto">
          <Search searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>
      </div>

      {/* Mobile Menu Modal */}
      {modals.mobileMenu && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="p-4 flex justify-end">
            <button 
              onClick={() => toggleModal("mobileMenu", false)}
              className="text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex flex-col space-y-4 p-4">
            {isAdmin && (
              <>
                <button
                  onClick={() => {
                    toggleModal("add", true);
                    toggleModal("mobileMenu", false);
                  }}
                  className="w-full py-3 bg-blue-500 text-white rounded flex items-center justify-center space-x-2"
                >
                  <Plus size={20} />
                  <span>Add Item</span>
                </button>
                <button
                  onClick={() => {
                    if (!selectedRow) {
                      alert("Please select a row to edit!");
                      return;
                    }
                    toggleModal("edit", true);
                    toggleModal("mobileMenu", false);
                  }}
                  className="w-full py-3 bg-yellow-500 text-white rounded flex items-center justify-center space-x-2"
                >
                  <Edit size={20} />
                  <span>Edit Item</span>
                </button>
                <button
                  onClick={() => {
                    if (!selectedRow) {
                      alert("Please select a row to delete!");
                      return;
                    }
                    handleDeleteConfirmation(selectedRow[rowKey]);
                    toggleModal("mobileMenu", false);
                  }}
                  className="w-full py-3 bg-red-500 text-white rounded flex items-center justify-center space-x-2"
                >
                  <Trash2 size={20} />
                  <span>Delete Item</span>
                </button>
              </>
            )}
            <Print data={data} />
          </div>
        </div>
      )}

      {/* Modal rendering */}
      {(modals.add || modals.edit || modals.delete || modals.search) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md mx-4">
            {renderModal(
              modals.add ? "add" 
              : modals.edit ? "edit"
              : modals.delete ? "delete"
              : modals.search ? "search"
              : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionBar;